/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { Box } from "@material-ui/core";
import React, {
  useState,
  useEffect,
  useCallback,
  ComponentProps,
  FC,
  SVGAttributes,
} from "react";
import { useDispatch } from "react-redux";

import {
  CommentFilterType,
  GiftItemData,
  streamingActions,
  StreamingType,
} from "../../modules/streaming";
import icon_comment_none from "../../designs/images/icon_comment_none.svg";
// import { StreamingCommentItem } from "./StreamingCommentItem";
import { authState } from "modules/auth";
import { StreamingCommentItem } from "components/streaming/StreamingCommentItem";
import { StreamingCommentItemV2 } from "./StreamingCommentItemV2";

import type { Timestamp } from "@google-cloud/firestore";
import type { THEOPlayer } from "@spwn/types/external";
import type { ProductData, UserMessage } from "@spwn/types/firebase/firestore";
import type { EventInfo } from "../../utility";

const getMsgListElement = () => {
  const innerElement = document.getElementById("msg-list-layout");
  const listElement = document.getElementById("msg-list-inner");
  return { innerElement, listElement };
};
/**
 * 取得したコメント欄のエレメントを基に、コメント欄の一番下までの高さを取得する
 * @param innerElement
 * @param listElement
 */
const getMsgListValue = (
  innerElement: HTMLElement,
  listElement: HTMLElement
) => {
  const msgAreaHeight = innerElement.scrollHeight;
  const msgDispleyFlame = listElement.clientHeight;
  const bottomPosition = msgAreaHeight - msgDispleyFlame;
  return bottomPosition;
};
/**
 * コメント欄の表示位置を一番下まで移動する
 */
const msgScrollBottom = () => {
  const { innerElement, listElement } = getMsgListElement();
  if (!innerElement || !listElement) return;
  const bottomPosition = getMsgListValue(innerElement, listElement);
  listElement.scrollTop = bottomPosition;
};

type Props = {
  eventStart: Timestamp;
  streamingType: StreamingType;
  player: THEOPlayer;
  eventInfo: EventInfo;
  giftItemMap: { [key: string]: GiftItemData };
  currentVideoId: string; // !!! don't allow null !!!
  displayComments: UserMessage[];
  eventGoodsMap: { [goodsId: string]: ProductData };
  user: authState["user"];
  isDarkModeEnabled: boolean;
  THEOPlayerElementHeight?: number;
  commentFilterState: CommentFilterType;
  banUser: ComponentProps<typeof StreamingCommentItem>["onClickBanUser"];
  banComment: ComponentProps<typeof StreamingCommentItem>["onClickBanComment"];
  deleteComment: ComponentProps<
    typeof StreamingCommentItem
  >["msgDeleteFunction"];
};

export const StreamingCommentLayout: React.FC<Props> = (props) => {
  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  // IDで重複を除去
  const uniqueComments = Array.from(
    new Map(props.displayComments.map((c) => [c._id, c])).values()
  );

  useEffect(() => {
    // scroll to bottom when auto scroll mode and displayComments is updated
    if (autoScroll) {
      msgScrollBottom();
    }
  }, [autoScroll, props.displayComments, props.THEOPlayerElementHeight]);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(streamingActions.setPauseCommentFetch(!autoScroll));
    return () => {
      dispatch(streamingActions.setPauseCommentFetch(false));
    };
  }, [autoScroll, dispatch]);

  const changeScrollState = useCallback(() => {
    const { innerElement, listElement } = getMsgListElement();
    // HOTFIX: テストの場合は onscroll を無視したい
    if (process.env.NODE_ENV !== "test") {
      // @ts-expect-error TS18047
      listElement.onscroll = () => {
        if (!innerElement || !listElement) return;
        const bottomPosition = getMsgListValue(innerElement, listElement);
        const scrollValue = listElement.scrollTop;
        // 1pxの誤差を許容する
        setAutoScroll(bottomPosition - scrollValue <= 1);
      };
    }
  }, []);

  useEffect(() => {
    changeScrollState();
    msgScrollBottom();
  }, [changeScrollState]);

  /**
   * ギフトアイテムの金額リストを作成する
   */
  const giftAmountList: number[] = [];
  if (props.giftItemMap) {
    Object.keys(props.giftItemMap).forEach((key) => {
      // @ts-expect-error TS2532
      const value = props.giftItemMap[key].values.paid;
      giftAmountList.push(value);
    });
    giftAmountList.sort((a, b) => a - b);
  }

  return (
    <Box
      position={"relative"}
      display={"flex"}
      flexDirection={"column"}
      height={"100%"}
      paddingBottom={"12px"}
      borderBottom={"1px solid #00000014"}
      borderColor={
        props.isDarkModeEnabled
          ? "dark.border.tertiary"
          : "light.border.tertiary"
      }
    >
      {/* コメント表示エリア */}
      <Box
        id={"msg-list-inner"}
        css={css`
          flex: 1 1 auto;
          width: 100%;
          height: 120px;
          overflow-y: auto;
          overscroll-behavior: none;
          ${props.isDarkModeEnabled
            ? "scrollbar-color: #5c5c5c #333333;"
            : undefined}
        `}
      >
        {props.commentFilterState === "none" ? (
          //中央に寄せるためにlayoutのBoxを分けている
          <Box
            id={"msg-list-layout"}
            height={"100%"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              height={"100%"}
              paddingTop={"12px"}
              alignItems={"center"}
              justifyContent={"center"}
              fontSize={"14px"}
              color={props.isDarkModeEnabled ? "#FFFFFF66" : "#00000066"}
            >
              <img
                src={icon_comment_none}
                alt=""
                aria-label={"コメントなしアイコン"}
                css={css``}
              />
            </Box>
          </Box>
        ) : (
          <Box id={"msg-list-layout"}>
            {uniqueComments.map((el) => {
              const isCommentUser = el.uid === props.user.uid;

              return (
                <StreamingCommentItemV2
                  key={el._id}
                  // @ts-expect-error TS2322
                  eventId={props.eventInfo._id}
                  message={el}
                  isCommentUser={isCommentUser}
                  isDarkModeEnabled={props.isDarkModeEnabled}
                  giftItemMap={props.giftItemMap}
                  eventGoodsMap={props.eventGoodsMap}
                  giftAmountList={giftAmountList}
                  onClickBanUser={props.banUser}
                  onClickBanComment={props.banComment}
                  msgDeleteFunction={props.deleteComment}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* 最新のコメントに戻るボタン */}
      <Box
        className={autoScroll ? "hide" : ""}
        css={css`
          position: absolute;
          bottom: 10px;
          right: 50%;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          color: ${props.isDarkModeEnabled ? "#000000" : "#ffffff"};
          font-size: 12px;
          font-weight: bold;
          background-color: ${props.isDarkModeEnabled ? "#ffffff" : "#000000"};
          border-radius: 8px;
          transform: translateX(50%);
          visibility: visible;
          opacity: 1;
          transition: 0.3s;
          cursor: pointer;
          box-shadow:
            0 2px 2px 0 rgba(0, 0, 0, 0.14),
            0 1px 5px 0 rgba(0, 0, 0, 0.12),
            0 3px 1px -2px rgba(0, 0, 0, 0.2);
          &.hide {
            visibility: hidden;
            opacity: 0;
          }
        `}
        onClick={() => setAutoScroll(true)}
      >
        <ArrowIcon width={"14px"} height={"14px"} />
        <Box>最新のコメント</Box>
      </Box>
    </Box>
  );
};

const ArrowIcon: FC<SVGAttributes<SVGElement>> = (props) => {
  return (
    <svg viewBox="0 0 12 12" {...props}>
      <path d="M10 6L9.295 5.295L6.5 8.085V2H5.5V8.085L2.71 5.29L2 6L6 10L10 6Z" />
    </svg>
  );
};
